/**
 * Registers a new block provided a unique name and an object defining its behavior.
 *
 * @see https://developer.wordpress.org/block-editor/developers/block-api/#registering-a-block
 */
import { registerBlockType } from '@wordpress/blocks';

/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * All files containing `style` keyword are bundled together. The code used
 * gets applied both to the front of your site and to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './style.scss';

/**
 * Internal dependencies
 */
import store from './store';
import Edit from './edit';
import save from './save';

/**
 * Every block starts by registering a new block type definition.
 *
 * @see https://developer.wordpress.org/block-editor/developers/block-api/#registering-a-block
 */
registerBlockType( 'vl-gutenberg/vl-block', {
	/**
	 * This is the display title for your block, which can be translated with `i18n` functions.
	 * The block inserter will show this name.
	 */
	title: __( 'Viral Loops', 'vl-gutenberg' ),

	/**
	 * This is a short description for your block, can be translated with `i18n` functions.
	 * It will be shown in the Block Tab in the Settings Sidebar.
	 */
	description: __(
		'Add a custom Viral Loops widget.', 'vl-gutenberg'
	),

	/**
	 * Blocks are grouped into categories to help users browse and discover them.
	 * The categories provided by core are `common`, `embed`, `formatting`, `layout` and `widgets`.
	 */
	category: 'embed',

	/**
	 * An icon property should be specified to make it easier to identify a block.
	 * These can be any of WordPress’ Dashicons, or a custom svg element.
	 */
	icon: <svg version="1.1" id="Layer_1" x="0px" y="0px"
	 width="24px" height="24px" viewBox="0 0 236.547 236.547" enable-background="new 0 0 236.547 236.547"
	 >
		<g id="Layer_5">
			<g>
				<g>
					<g>
						<path fill="#FFFFFF" d="M235.6,118.037c0,64.934-52.635,117.563-117.563,117.563c-64.927,0-117.562-52.631-117.562-117.563
							c0-64.924,52.635-117.563,117.562-117.563C182.965,0.474,235.6,53.113,235.6,118.037z"/>
					</g>
				</g>
				<g>
					<path fill="none" d="M63.054,116.665c0-1.016,0.177-1.989,0.489-2.899c-4.569-5.17-9.138-10.34-13.706-15.51
						c0.01,0.26,0.016,0.52,0.027,0.78c0.322,7.506,1.038,14.996,1.929,22.455c0.682,5.712,1.444,11.43,2.464,17.097
						c3.343-5.802,6.686-11.603,10.03-17.403C63.509,119.857,63.054,118.315,63.054,116.665z"/>
					<path fill="none" d="M80.559,114.029c13.286,2.947,26.572,5.894,39.858,8.841c1.771-3.65,4.696-6.626,8.312-8.456
						c-4.191-18.401-8.382-36.801-12.572-55.202C104.11,77.042,92.065,94.871,80.019,112.7
						C80.23,113.126,80.416,113.568,80.559,114.029z"/>
					<path fill="none" d="M124.758,57.114c4.199,18.438,8.398,36.875,12.598,55.314c3.122,0.048,6.058,0.861,8.623,2.273
						c9.693-10.676,19.387-21.351,29.08-32.025c-0.89-1.727-1.401-3.681-1.401-5.757c0-0.869,0.088-1.717,0.256-2.537
						c-10.189-5.348-21.095-9.635-31.722-13.781c-5.047-1.971-10.128-3.918-15.29-5.585C126.286,55.81,125.564,56.514,124.758,57.114z
						"/>
					<path fill="none" d="M152.489,120.694c1.951,2.933,3.093,6.45,3.093,10.236c0,1.7-0.249,3.34-0.678,4.903
						c6.381,3.504,12.762,7.007,19.143,10.51c1.618-1.391,3.587-2.38,5.761-2.808c1.503-14.573,2.408-29.24,2.845-43.88
						c0.104-3.527,0.203-7.071,0.198-10.611c-0.423-0.119-0.837-0.257-1.24-0.418C171.903,99.315,162.196,110.004,152.489,120.694z"/>
					<path fill="none" d="M118.581,131.529c-13.145-2.915-26.29-5.831-39.436-8.747c7.509,19.078,15.018,38.156,22.527,57.233
						c7.417-12.017,14.833-24.035,22.25-36.054C120.735,140.749,118.736,136.372,118.581,131.529z"/>
					<path fill="none" d="M169.645,155.904c0-0.61,0.059-1.206,0.143-1.794c-6.404-3.516-12.809-7.032-19.213-10.548
						c-3.379,3.611-8.173,5.882-13.506,5.882c-1.948,0-3.824-0.305-5.588-0.862c-7.027,11.387-14.055,22.774-21.083,34.161
						c0.463-0.157,0.929-0.309,1.391-0.47c13.809-4.819,27.391-10.382,40.788-16.235c5.803-2.535,11.595-5.118,17.273-7.925
						C169.723,157.394,169.645,156.659,169.645,155.904z"/>
					<path fill="none" d="M55.104,87.409c0,1.015-0.178,1.988-0.49,2.898c5.184,5.866,10.368,11.732,15.551,17.599
						c0.594-0.124,1.208-0.191,1.839-0.191c0.233,0,0.461,0.018,0.689,0.035c11.454-16.955,22.909-33.91,34.365-50.864
						c-9.507,4.227-18.676,9.558-27.645,14.58c-6.389,3.579-12.74,7.233-18.959,11.102c-1.712,1.066-3.656,2.233-5.475,3.412
						C55.054,86.446,55.104,86.921,55.104,87.409z"/>
					<path fill="none" d="M71.266,126.821c-3.648,6.33-7.296,12.661-10.945,18.991c0.779,1.327,1.233,2.868,1.233,4.519
						c0,0.973-0.16,1.907-0.448,2.784c8.894,9.118,18.676,17.719,28.811,25.312c0.774,0.58,1.554,1.156,2.339,1.728
						C85.259,162.376,78.262,144.599,71.266,126.821z"/>
					<path fill="#2F2BAD" d="M198.873,76.919c0-6.963-5.648-12.61-12.609-12.61c-2.889,0-5.543,0.983-7.67,2.62
						c-0.094-0.047-0.196-0.096-0.289-0.143c-13.609-6.859-27.865-12.678-42.172-17.913c-2.336-0.855-4.705-1.738-7.101-2.535
						c-0.92-5.15-5.412-9.063-10.826-9.063c-5.752,0-10.467,4.415-10.959,10.04c-12.266,5.227-24.033,11.807-35.607,18.36
						c-6.121,3.463-12.19,7.029-18.135,10.797c-1.329,0.842-2.736,1.695-4.125,2.595c-1.003-0.389-2.089-0.611-3.229-0.611
						c-4.94,0-8.949,4.012-8.949,8.953c0,2.97,1.456,5.596,3.686,7.224c0.011,0.509,0.022,1.005,0.029,1.482
						c0.097,7.2,0.811,14.407,1.58,21.566c0.814,7.543,1.815,15.079,3.091,22.562c0.204,1.19,0.385,2.51,0.597,3.858
						c-1.564,1.611-2.531,3.807-2.531,6.229c0,4.942,4.007,8.949,8.95,8.949c0.627,0,1.239-0.065,1.83-0.188
						c1.541,1.487,3.126,2.921,4.614,4.34c5.678,5.409,11.553,10.625,17.576,15.647c4.871,4.062,9.877,8.133,15.271,11.509
						c0.376,4.594,4.22,8.214,8.913,8.214c4.006,0,7.398-2.638,8.539-6.268c10.311-2.923,20.399-7.467,30.22-11.471
						c10.277-4.191,20.498-8.557,30.545-13.271c1.312-0.616,2.834-1.25,4.421-1.938c2.135,1.659,4.809,2.657,7.72,2.657
						c6.962,0,12.607-5.648,12.607-12.606c0-4.684-2.56-8.763-6.351-10.938c0.658-5.901,1.129-11.827,1.554-17.749
						c0.936-12.923,1.761-25.955,1.62-38.93C195.93,86.258,198.873,81.936,198.873,76.919z M182.652,99.655
						c-0.437,14.64-1.342,29.307-2.845,43.88c-2.174,0.428-4.143,1.417-5.761,2.808c-6.381-3.503-12.762-7.006-19.143-10.51
						c0.429-1.563,0.678-3.203,0.678-4.903c0-3.786-1.142-7.304-3.093-10.236c9.707-10.689,19.414-21.379,29.121-32.068
						c0.403,0.161,0.817,0.299,1.24,0.418C182.855,92.584,182.757,96.128,182.652,99.655z M101.672,180.015
						c-7.509-19.077-15.018-38.155-22.527-57.233c13.146,2.916,26.291,5.832,39.436,8.747c0.155,4.843,2.154,9.22,5.341,12.433
						C116.505,155.98,109.089,167.999,101.672,180.015z M54.258,138.587c-1.021-5.667-1.783-11.385-2.464-17.097
						c-0.891-7.458-1.607-14.949-1.929-22.455c-0.011-0.26-0.018-0.52-0.027-0.78c4.568,5.17,9.136,10.34,13.706,15.51
						c-0.312,0.91-0.489,1.883-0.489,2.899c0,1.65,0.455,3.192,1.234,4.519C60.944,126.985,57.601,132.786,54.258,138.587z
						 M116.156,59.213c4.19,18.4,8.381,36.801,12.572,55.202c-3.615,1.83-6.54,4.806-8.312,8.456
						c-13.286-2.947-26.572-5.895-39.858-8.841c-0.143-0.461-0.328-0.903-0.54-1.329C92.065,94.871,104.11,77.042,116.156,59.213z
						 M142.191,60.601c10.627,4.146,21.532,8.434,31.722,13.781c-0.168,0.819-0.256,1.667-0.256,2.537c0,2.076,0.512,4.03,1.401,5.757
						c-9.693,10.674-19.387,21.35-29.08,32.025c-2.565-1.412-5.501-2.226-8.623-2.273c-4.199-18.438-8.398-36.876-12.598-55.314
						c0.807-0.6,1.528-1.304,2.144-2.098C132.063,56.683,137.145,58.63,142.191,60.601z M79.413,71.465
						c8.969-5.022,18.138-10.353,27.645-14.58C95.603,73.84,84.147,90.794,72.693,107.75c-0.228-0.017-0.457-0.035-0.689-0.035
						c-0.63,0-1.245,0.067-1.839,0.191c-5.183-5.867-10.367-11.733-15.551-17.599c0.312-0.91,0.49-1.883,0.49-2.898
						c0-0.488-0.05-0.963-0.125-1.43c1.819-1.179,3.763-2.346,5.475-3.412C66.673,78.698,73.024,75.044,79.413,71.465z
						 M61.106,153.115c0.288-0.877,0.448-1.812,0.448-2.784c0-1.65-0.455-3.191-1.233-4.519c3.648-6.33,7.297-12.661,10.945-18.991
						c6.997,17.778,13.993,35.556,20.991,53.333c-0.785-0.571-1.565-1.147-2.339-1.728C79.783,170.833,70,162.233,61.106,153.115z
						 M111.789,182.273c-0.462,0.161-0.928,0.312-1.391,0.47c7.028-11.387,14.055-22.774,21.083-34.161
						c1.764,0.558,3.64,0.862,5.588,0.862c5.333,0,10.127-2.271,13.506-5.882c6.404,3.516,12.809,7.032,19.213,10.548
						c-0.084,0.588-0.143,1.184-0.143,1.794c0,0.755,0.078,1.49,0.206,2.209c-5.679,2.807-11.471,5.39-17.273,7.925
						C139.18,171.891,125.598,177.454,111.789,182.273z"/>
				</g>
			</g>
		</g>
	</svg>,

	/**
	 * Optional block extended support features.
	 */
	supports: {
		// Removes support for an HTML mode.
		//html: false,
	},
	
	/**
	 * Block attributes.
	 */	
	/*attributes: {
		content: {type: 'object'},
		color: {type: 'string'},
		categories: {type: 'string'},
		selectedCategory: {type: 'string'},
	},*/

	/**
	 * @see ./edit.js
	 */
	edit: Edit,

	/**
	 * @see ./save.js
	 */
	save,
} );
